import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Newspaper, Shield, Trophy, X } from 'lucide-react';
import { news, newsError, type NewsPage } from '../services/news';
import { recentPro, recentError, type RecentPage } from '../services/recentPro';
import { latestRecentProResults, recentProDayWindow, shiftUtcDay } from './demos/recentProDays';
import { recentProLink } from './demos/recentProLink';
import { proMapName } from './demos/ProMapArtwork';
import './MainMenuFeeds.css';

function Logo({ src }: { src?: string | null }) {
  const [failed, setFailed] = useState(false);
  return src && !failed ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setFailed(true)}/> : <Shield size={20}/>;
}

function RecentResults() {
  const { oldestDay, latestDay } = recentProDayWindow();
  const [selectedDate, setDate] = useState<string | null>(null);
  const [date, setResolvedDate] = useState(latestDay);
  const [data, setData] = useState<RecentPage | null>(null);
  const [error, setError] = useState(''), [loading, setLoading] = useState(true), [revision, setRevision] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(''); setData(null); list.current?.scrollTo(0, 0);
    const loadDay = (day: string) => recentPro.list(1, false, controller.signal, day);
    const request = selectedDate ? loadDay(selectedDate).then(data => ({ date: selectedDate, data }))
      : latestRecentProResults(loadDay, controller.signal);
    request.then(result => { if (!controller.signal.aborted) { setResolvedDate(result.date); setData(result.data); } })
      .catch(e => { if (!controller.signal.aborted) setError(recentError(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [selectedDate, revision]);
  return <>
    <nav className="hub-feed-date" aria-label="Pro match date">
      <button aria-label="Previous day" disabled={date <= oldestDay || loading} onClick={() => setDate(shiftUtcDay(date, -1))}><ChevronLeft size={16}/></button>
      <span><strong>{new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })}</strong><small>Recent 28 Days · UTC</small></span>
      <button aria-label="Next day" disabled={date >= latestDay || loading} onClick={() => setDate(shiftUtcDay(date, 1))}><ChevronRight size={16}/></button>
    </nav>
    <div className="hub-feed-scroll" ref={list} aria-busy={loading}>
      {loading && <p role="status" className="hub-feed-empty">Loading matches…</p>}
      {error && <div role="alert" className="hub-feed-empty"><p>{error}</p><button onClick={() => setRevision(n => n + 1)}>Try again</button></div>}
      {!loading && !error && !data?.items.length && <p className="hub-feed-empty">No matches for this day. Browse previous days using the arrows.</p>}
      {data?.items.map(match => <article className="hub-match" key={match.id}>
        <p className="hub-match-event">{match.eventName}</p>
        <div className="hub-match-teams"><span><Logo key={match.team1LogoUrl} src={match.team1LogoUrl}/><strong>{match.team1Name}</strong></span><small>vs</small><span><Logo key={match.team2LogoUrl} src={match.team2LogoUrl}/><strong>{match.team2Name}</strong></span></div>
        <div className="hub-match-maps">{match.demos.map(demo => {
          const map = proMapName(demo.mapName);
          return <div className="hub-match-map" key={demo.id} style={map ? { backgroundImage: `linear-gradient(90deg,rgba(5,12,25,.8),rgba(5,12,25,.75)),url("${map.image}")` } : undefined}>
            <span>{map?.name || demo.mapName}</span><strong>{demo.team1Score != null && demo.team2Score != null ? `${demo.team1Score} : ${demo.team2Score}` : '—'}</strong>
          </div>;
        })}</div>
        {!match.demos.length && <p className="hub-match-pending">Maps / demos not available yet</p>}
        <Link className="hub-feed-link" to={recentProLink(match.id, match.matchDate)}>Open in Pro Demos <ChevronRight size={14}/></Link>
      </article>)}
    </div>
  </>;
}

function NewsFeed() {
  const [page, setPage] = useState(1), [data, setData] = useState<NewsPage | null>(null);
  const [error, setError] = useState(''), [loading, setLoading] = useState(true), [revision, setRevision] = useState(0);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const controller = new AbortController(); setLoading(true); setError(''); setData(null); list.current?.scrollTo(0, 0);
    news.list(page, false, controller.signal).then(result => { if (!controller.signal.aborted) setData(result); })
      .catch(e => { if (!controller.signal.aborted) setError(newsError(e)); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [page, revision]);
  return <><div className="hub-feed-scroll" ref={list} aria-busy={loading}>
    {loading && <p role="status" className="hub-feed-empty">Loading news…</p>}
    {error && <div role="alert" className="hub-feed-empty"><p>{error}</p><button onClick={() => setRevision(n => n + 1)}>Try again</button></div>}
    {!loading && !error && !data?.items.length && <p className="hub-feed-empty">New features and app updates will appear here.</p>}
    {data?.items.map(entry => <Link className="hub-news-card" key={entry.id} to={`/news/${entry.id}`}>
      {entry.imageUrls[0] && <img src={entry.imageUrls[0]} alt="" loading="lazy"/>}
      <div><time>{entry.publishedAt && new Date(entry.publishedAt).toLocaleDateString()}</time><h3>{entry.title}</h3><p>{entry.summary}</p><span>Read update <ChevronRight size={13}/></span></div>
    </Link>)}
  </div>{(page > 1 || data?.hasMore) && <nav className="hub-feed-pagination" aria-label="News pages"><button disabled={page === 1 || loading} onClick={() => setPage(p => p - 1)}>Newer</button><span>{page}</span><button disabled={!data?.hasMore || loading} onClick={() => setPage(p => p + 1)}>Older</button></nav>}</>;
}

export function MainMenuFeeds({ hasClip, isAdmin }: { hasClip: boolean; isAdmin: boolean }) {
  const [open, setOpen] = useState({ matches: false, news: false });
  const root = useRef<HTMLDivElement>(null);
  const toggle = (key: 'matches' | 'news') => setOpen(state => ({ ...state, [key]: !state[key] }));
  return <div ref={root} className={`hub-feeds${hasClip ? ' hub-feeds--clip' : ''}${isAdmin ? ' hub-feeds--admin' : ''}`} onKeyDown={e => {
    if (e.key === 'Escape') {
      const panel = (e.target as HTMLElement).closest('aside');
      if (panel) { const key = panel.dataset.feed as 'matches' | 'news'; setOpen(state => ({ ...state, [key]: false })); panel.querySelector<HTMLButtonElement>('.hub-feed-toggle')?.focus(); }
    }
  }}>
    {(['matches', 'news'] as const).map(key => <aside key={key} data-feed={key} className={`hub-feed hub-feed--${key}${open[key] ? ' is-open' : ''}`}>
      <button className="hub-feed-toggle" aria-expanded={open[key]} aria-controls={`hub-${key}`} onClick={() => toggle(key)}>
        {key === 'matches' ? <Trophy size={17}/> : <Newspaper size={17}/>}<span>{key === 'matches' ? 'Pro Results' : 'What’s New'}</span>{open[key] ? <X size={16}/> : key === 'matches' ? <ChevronRight size={16}/> : <ChevronLeft size={16}/>}
      </button>
      {open[key] && <section id={`hub-${key}`} className="hub-feed-panel" aria-label={key === 'matches' ? 'Recent Pro Results' : 'StratSync News'}>
        <header><span>{key === 'matches' ? 'RECENT PRO RESULTS' : 'STRATSYNC NEWS'}</span></header>
        {key === 'matches' ? <RecentResults/> : <NewsFeed/>}
      </section>}
    </aside>)}
  </div>;
}
